import { useState } from "react";

function formatValue(value, suffix = "") {
  if (value === null || value === undefined || value === "") return "-";
  return `${value}${suffix}`;
}

function firstPresent(...values) {
  return values.find((value) => value !== null && value !== undefined && value !== "");
}

function findMarketRow(rows, names) {
  const aliases = Array.isArray(names) ? names : [names];
  const normalized = aliases.map((name) => String(name ?? "").toLowerCase());
  return (rows ?? []).find((row) => {
    const rowText = [
      row?.name,
      row?.symbol,
      row?.sourceCode,
      row?.headline,
    ].filter(Boolean).join(" ").toLowerCase();
    return normalized.some((name) => rowText.includes(name));
  });
}

function findMarketSection(sections, sectionCode) {
  const normalizedCode = String(sectionCode ?? "").toUpperCase();
  return (sections ?? []).find((section) => String(section?.sectionCode ?? "").toUpperCase() === normalizedCode);
}

function normalizeMarketRow(row) {
  if (!row) return null;
  const summaryValues = parseQuoteSummary(row.summary ?? row.adminNotes);

  return {
    ...row,
    name: firstPresent(row.name, row.headline, row.sourceCode, row.symbol),
    symbol: firstPresent(row.symbol, row.sourceCode, row.name, row.headline),
    price: firstPresent(row.price, row.currentValue, summaryValues.currentValue),
    previousClose: firstPresent(row.previousClose, row.previousValue, summaryValues.previousValue),
    change: firstPresent(row.change, row.changeValue, summaryValues.changeValue),
    changePct: firstPresent(row.changePct, row.changePercent, summaryValues.changePercent),
    currency: firstPresent(row.currency, row.currencyCode, "USD"),
  };
}

function parseQuoteSummary(summary) {
  const text = String(summary ?? "");
  if (!text) return {};

  return {
    previousValue: parseSummaryNumber(text, /(?:^|\|)\s*Prev\s+(-?\d+(?:\.\d+)?)/i),
    currentValue: parseSummaryNumber(text, /(?:^|\|)\s*Current\s+(-?\d+(?:\.\d+)?)/i),
    changeValue: parseSummaryNumber(text, /(?:^|\|)\s*Change\s+(-?\d+(?:\.\d+)?)(?!\s*%)/i),
    changePercent: parseSummaryNumber(text, /(?:^|\|)\s*Change\s*%\s+(-?\d+(?:\.\d+)?)%?/i),
  };
}

function parseSummaryNumber(text, pattern) {
  const match = text.match(pattern);
  if (!match) return undefined;

  const number = Number(match[1]);
  return Number.isFinite(number) ? number : undefined;
}

function getSectionMarketRows(marketBias, sectionCode) {
  const section = findMarketSection(marketBias?.marketBiasSections, sectionCode);
  return (section?.items ?? []).map(normalizeMarketRow).filter(Boolean);
}

function getLegacyMarketRows(rows, aliases) {
  return aliases.map((names) => normalizeMarketRow(findMarketRow(rows, names))).filter(Boolean);
}

function formatMarketPrice(row) {
  const price = row?.price;
  if (price === null || price === undefined || price === "") return "-";

  const currency = row.currency ?? row.currencyCode ?? "USD";
  const numericPrice = Number(price);

  if (!Number.isFinite(numericPrice)) {
    return `${price} ${currency}`;
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(numericPrice);
}

function formatChangePercent(row) {
  const value = row?.changePct;
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string" && value.trim().endsWith("%")) return value;

  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return `${number.toFixed(2)}%`;
}

function changeClassName(row) {
  return String(row?.changePct ?? row?.change ?? "").startsWith("-") ? "down" : "up";
}

function MarketProxyRows({ title, rows, fallback }) {
  const values = rows.filter(Boolean);

  return (
    <div className="market-proxy-section">
      <div className="market-proxy-title">
        <span>{title}</span>
        <em>{values.length}</em>
      </div>
      {values.length ? (
        <div className="market-proxy-list">
          {values.map((row) => (
            <div className="market-proxy-row" key={`${title}-${row.inputId ?? row.symbol ?? row.name}`}>
              <div>
                <strong>{String(row.symbol ?? row.name ?? "-").toUpperCase()}</strong>
                <span>{String(row.name ?? "").toUpperCase()}</span>
              </div>
              <b>{formatMarketPrice(row)}</b>
              <em className={changeClassName(row)}>{formatChangePercent(row)}</em>
            </div>
          ))}
        </div>
      ) : (
        <p className="evidence-empty">{fallback}</p>
      )}
    </div>
  );
}

function formatFactItems(facts) {
  if (typeof facts === "string") return facts ? [facts] : [];
  if (!Array.isArray(facts)) return [];
  return facts.filter(Boolean);
}

function EvidenceGroup({ title, items, fallback, defaultVisible = 2, collapsedByDefault = false }) {
  const [expanded, setExpanded] = useState(false);
  const [collapsed, setCollapsed] = useState(collapsedByDefault);
  const values = formatFactItems(items);
  const visibleItems = collapsed
    ? []
    : expanded
      ? values
      : values.slice(0, defaultVisible);
  const hiddenCount = Math.max(values.length - visibleItems.length, 0);

  return (
    <div className="evidence-group">
      <div className="evidence-group-title">
        <span>{title}</span>
        <em>{values.length}</em>
      </div>
      {visibleItems.length ? (
        <div className="evidence-list">
          {visibleItems.map((item, index) => (
            <p key={`${title}-${index}`}>{item}</p>
          ))}
        </div>
      ) : !collapsed ? (
        <p className="evidence-empty">{fallback}</p>
      ) : null}
      {collapsed && values.length > 0 && (
        <button
          type="button"
          className="evidence-toggle"
          onClick={() => setCollapsed(false)}
        >
          Show {values.length}
        </button>
      )}
      {!collapsed && values.length > defaultVisible && (
        <button
          type="button"
          className="evidence-toggle"
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? "Show less" : `+${hiddenCount} more`}
        </button>
      )}
    </div>
  );
}

const MARKET_INTELLIGENCE_FALLBACKS = {
  etfs: "ETF confirmation checks broad risk appetite through global and India-linked proxies before validating the daily plan.",
  commodities: "Commodity confirmation watches crude oil, metals, and dollar-linked pressure that can change sector risk.",
  globalEvents: "Maintained global events are checked for market impact, escalation risk, and whether news flow supports or weakens the plan.",
  indiaEvents: "India news is checked for Nifty/Sensex direction, RBI or macro pressure, FII/DII flow, rupee risk, and sector cues.",
  news: "News evidence explains why the final market score supports the current primary and secondary strategy choices.",
};

function ListBlock({ title, items, onSelect, tone = "neutral", defaultVisible = 3 }) {
  const [expanded, setExpanded] = useState(false);
  if (!items?.length) return null;

  const visibleItems = expanded ? items : items.slice(0, defaultVisible);
  const hiddenCount = Math.max(items.length - visibleItems.length, 0);

  return (
    <div className={`strategy-list-block strategy-list-${tone}`}>
      <span>{title} <em>{items.length}</em></span>
      <div className="strategy-pill-row">
        {visibleItems.map((item) => (
          <button
            type="button"
            key={item}
            className="strategy-pill-btn"
            onClick={() => onSelect?.(item)}
          >
            {item}
          </button>
        ))}
        {items.length > defaultVisible && (
          <button
            type="button"
            className="strategy-pill-btn strategy-pill-more"
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? "Show less" : `+${hiddenCount} more`}
          </button>
        )}
      </div>
    </div>
  );
}

export default function DecisionDetailsCard({ marketBias, strategy, loading, onStrategySelect }) {
  const mode = strategy?.mode ?? "Balanced";
  const rows = marketBias?.marketRows ?? [];
  const etfRows = getSectionMarketRows(marketBias, "ETF_INPUTS");
  const commodityRows = getSectionMarketRows(marketBias, "COMMODITY_INPUTS");
  const legacyEtfRows = getLegacyMarketRows(rows, [
    ["sp500", "s&p", "spy"],
    ["nasdaq", "qqq"],
    ["dow", "dia"],
  ]);
  const legacyCommodityRows = getLegacyMarketRows(rows, [
    ["gold", "gld"],
    ["copper", "cper"],
    ["crude", "oil", "uco", "uso"],
  ]);
  const globalEventItems = formatFactItems(marketBias?.eventImpact?.facts);
  const indiaEventItems = formatFactItems(marketBias?.indiaBiasDetails?.facts);
  const newsItems = formatFactItems(marketBias?.reasons);
  const recentTradingDays = (marketBias?.recentMarketNotes ?? [])
    .slice(0, 5);

  return (
    <section className="desk-card decision-details-card support-card">
      <div className="card-header">
        <div>
          <h2>Market Decision Desk</h2>
          {loading && <p>Loading the current trade map...</p>}
        </div>
      </div>

      <div className="decision-details-grid">
        <section className="decision-stack">
          <div className="decision-panel-title">
            <strong>Market Intelligence</strong>
          </div>

            <div className="market-driver-panel decision-driver-panel">
              <div className="market-proxy-group">
              <MarketProxyRows title="ETFs" rows={etfRows.length ? etfRows : legacyEtfRows} fallback={MARKET_INTELLIGENCE_FALLBACKS.etfs} />
              <MarketProxyRows title="Commodities" rows={commodityRows.length ? commodityRows : legacyCommodityRows} fallback={MARKET_INTELLIGENCE_FALLBACKS.commodities} />
            </div>

            <div className="decision-context-block decision-wtd-block market-intelligence-wtd">
              <span>Weekly To Date</span>
              <strong>Last 5 Trading Days</strong>
              <p>
                {marketBias?.weeklyTrendNarration ?? "Recent weekly movement is used as a background check."}
              </p>
              {!!recentTradingDays.length && (
                <div className="decision-wtd-list">
                  {recentTradingDays.map((note) => (
                    <div className="decision-wtd-day" key={note.date}>
                      <strong>{note.label ?? note.date}</strong>
                      <span>Global {note.global ?? "-"}</span>
                      <span>NSE {note.nse ?? "-"}</span>
                      <em>{formatValue(note.equityAverageChangePct, "%")}</em>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="event-intelligence-stack">
              <EvidenceGroup title="Global Events" items={globalEventItems} fallback={MARKET_INTELLIGENCE_FALLBACKS.globalEvents} />
              <EvidenceGroup title="India Events" items={indiaEventItems} fallback={MARKET_INTELLIGENCE_FALLBACKS.indiaEvents} />
              <EvidenceGroup title="News" items={newsItems} fallback={MARKET_INTELLIGENCE_FALLBACKS.news} collapsedByDefault />
            </div>
          </div>
        </section>

        <section className="decision-stack">
          <div className="decision-panel-title">
            <strong>Execution Plan</strong>
          </div>

          <div className="decision-stack-header">
            <div>
              <span>{mode}</span>
              <strong>{strategy?.primaryStrategy ?? "-"}</strong>
            </div>
            <button
              type="button"
              className="decision-primary-capsule"
              onClick={() => onStrategySelect?.(strategy?.primaryStrategy)}
            >
              <span>Primary</span>
              <strong>Open</strong>
            </button>
          </div>

          <div className="table-summary-grid decision-mini-grid">
            <div><span>Min Score</span><strong>{strategy?.minScore ?? "-"}</strong></div>
            <div><span>Position Size</span><strong>{strategy?.positionSizing ?? "-"}</strong></div>
          </div>

          <div className="strategy-plan-details">
            <article>
              <span>Entry Rule</span>
              <p>{strategy?.entryRule ?? "-"}</p>
            </article>
            <article>
              <span>Exit Rule</span>
              <p>{strategy?.exitRule ?? "-"}</p>
            </article>
            <article>
              <span>Reason</span>
              <p>{strategy?.reason ?? "-"}</p>
            </article>
            {!!strategy?.actions?.length && (
              <article>
                <span>Actions</span>
                <ul>
                  {strategy.actions.map((action) => (
                    <li key={action}>{action}</li>
                  ))}
                </ul>
              </article>
            )}
          </div>

          <div className="strategy-table-lists decision-lists">
            <ListBlock title="Secondary Strategies" items={strategy?.secondaryStrategies} onSelect={onStrategySelect} />
            <ListBlock title="Avoid Strategies" items={strategy?.avoidStrategies} onSelect={onStrategySelect} tone="avoid" />
          </div>

          <button
            type="button"
            className="primary-action-btn decision-stock-list-btn"
            onClick={() => onStrategySelect?.(strategy?.primaryStrategy)}
          >
            View Trade Candidates
          </button>
        </section>
      </div>
    </section>
  );
}
